import type { ReactNode } from 'react';

/** Иконки интерфейса одним набором: контур 24×24 в цвет текста, залитые помечены `fill`. */

type Shape = { fill?: boolean; body: ReactNode };

const ICONS = {
  home: {
    fill: true,
    body: <path d="M11.3 2.6a1 1 0 0 1 1.4 0l8 7.4c.6.6.2 1.7-.7 1.7H19v7.8a1.5 1.5 0 0 1-1.5 1.5H15a.5.5 0 0 1-.5-.5v-4.4a1 1 0 0 0-1-1h-3a1 1 0 0 0-1 1v4.4a.5.5 0 0 1-.5.5H6.5A1.5 1.5 0 0 1 5 19.5v-7.8H4c-.9 0-1.3-1.1-.7-1.7l8-7.4Z" />,
  },
  grid: {
    fill: true,
    body: (
      <>
        <rect x="3" y="3" width="8" height="8" rx="2.2" />
        <rect x="13" y="3" width="8" height="8" rx="2.2" />
        <rect x="3" y="13" width="8" height="8" rx="2.2" />
        <rect x="13" y="13" width="8" height="8" rx="2.2" />
      </>
    ),
  },
  person: {
    fill: true,
    body: (
      <>
        <circle cx="12" cy="7.5" r="4.2" />
        <path d="M4 20.2c0-3.8 3.6-6.2 8-6.2s8 2.4 8 6.2c0 .6-.5 1-1.1 1H5.1c-.6 0-1.1-.4-1.1-1Z" />
      </>
    ),
  },
  search: {
    body: (
      <>
        <circle cx="10.8" cy="10.8" r="6.3" />
        <path d="m15.6 15.6 4.9 4.9" />
      </>
    ),
  },
  bell: {
    fill: true,
    body: <path d="M12 2.5a6.5 6.5 0 0 0-6.5 6.5v3.2l-1.6 3.1c-.4.8.2 1.7 1.1 1.7h14c.9 0 1.5-.9 1.1-1.7l-1.6-3.1V9A6.5 6.5 0 0 0 12 2.5Zm-2.6 16.3a2.7 2.7 0 0 0 5.2 0H9.4Z" />,
  },
  chevronLeft: { body: <path d="m14.5 5-7 7 7 7" /> },
  chevronRight: { body: <path d="m9.5 5 7 7-7 7" /> },
  chevronDown: { body: <path d="m5 9 7 7 7-7" /> },
  plus: { body: <path d="M12 5v14M5 12h14" /> },
  minus: { body: <path d="M5 12h14" /> },
  close: { body: <path d="M6 6l12 12M18 6 6 18" /> },
  check: { body: <path d="m5 12.5 4.5 4.5L19 7.5" /> },
  trash: {
    body: (
      <>
        <path d="M4 7h16M9.5 7V4.8c0-.4.4-.8.8-.8h3.4c.4 0 .8.4.8.8V7" />
        <path d="M6.2 7 7 19.2c0 .5.4.8.9.8h8.2c.5 0 .9-.3.9-.8L17.8 7M10 11v5.5M14 11v5.5" />
      </>
    ),
  },
  heart: { body: <path d="M12 20.3S4 15.4 4 9.6A4.3 4.3 0 0 1 12 7.4a4.3 4.3 0 0 1 8 2.2c0 5.8-8 10.7-8 10.7Z" /> },
  heartFill: {
    fill: true,
    body: <path d="M12 20.8c-.3 0-.5-.1-.7-.2C9.600 19.500 3.500 15 3.500 9.600A4.800 4.800 0 0 1 12 6.400a4.800 4.800 0 0 1 8.500 3.200c0 5.400-6.100 9.900-7.800 11-.2.100-.4.200-.7.200Z" />,
  },
  share: {
    body: (
      <>
        <path d="M12 15V3.5M8 7l4-3.8L16 7" />
        <path d="M7.500 10.500H6.800c-.7 0-1.300.6-1.300 1.300v7.400c0 .7.6 1.300 1.300 1.300h10.400c.7 0 1.300-.6 1.300-1.300v-7.400c0-.7-.6-1.300-1.300-1.300h-.7" />
      </>
    ),
  },
  info: {
    body: (
      <>
        <path d="M12 11v7M10 11h2M10 18h4" />
        <circle cx="12" cy="6.500" r="0.600" fill="currentColor" />
      </>
    ),
  },
  cart: {
    body: (
      <>
        <circle cx="9" cy="20" r="1.400" />
        <circle cx="17" cy="20" r="1.400" />
        <path d="M3 4h2l2.500 11h10L20 7H6" />
      </>
    ),
  },
  bike: {
    body: (
      <>
        <circle cx="5.500" cy="16.500" r="3.500" />
        <circle cx="18.500" cy="16.500" r="3.500" />
        <path d="M5.500 16.500 9 9h6l3.500 7.500M9 9 8 6.500H6M12.500 16.500 15 9l1-2.500h2.200" />
      </>
    ),
  },
  box: {
    fill: true,
    body: <path d="M12.500 2.700a1 1 0 0 0-1 0L4.600 6.400l7.400 3.900 7.400-3.900-6.900-3.700ZM3.500 8v8.500c0 .4.200.7.500.9l7 3.700v-9.200L3.500 8Zm17 0L13 11.900v9.200l7-3.700c.300-.2.500-.5.500-.9V8Z" />,
  },
  percent: {
    body: (
      <>
        <path d="M18 6 6 18" />
        <circle cx="7.500" cy="7.500" r="2.300" />
        <circle cx="16.500" cy="16.500" r="2.300" />
      </>
    ),
  },
  sparkles: {
    fill: true,
    body: <path d="M10 3.500c.3 3.800 1.700 5.200 5.500 5.500-3.800.3-5.200 1.700-5.500 5.500-.3-3.800-1.700-5.200-5.500-5.500 3.800-.3 5.200-1.700 5.500-5.500Zm7.500 9.500c.2 2.400 1.100 3.300 3.500 3.500-2.400.2-3.300 1.100-3.500 3.500-.2-2.400-1.100-3.300-3.500-3.500 2.400-.2 3.300-1.100 3.500-3.500Z" />,
  },
  history: {
    body: (
      <>
        <path d="M4.500 12a7.500 7.500 0 1 0 2.300-5.400L4.500 8.800" />
        <path d="M4.300 4.500v4.400h4.400" />
      </>
    ),
  },
  clock: {
    body: (
      <>
        <circle cx="12" cy="12" r="8.500" />
        <path d="M12 7.500V12l3 2" />
      </>
    ),
  },
  tag: {
    body: (
      <>
        <path d="M3.500 12.200V5c0-.8.700-1.500 1.500-1.500h7.200c.4 0 .8.200 1.100.4l7 7c.6.600.6 1.500 0 2.100l-7.200 7.200c-.6.600-1.500.6-2.100 0l-7-7a1.500 1.500 0 0 1-.5-1Z" />
        <circle cx="8" cy="8" r="1.300" />
      </>
    ),
  },
  gift: {
    fill: true,
    body: <path d="M9 2.500A3 3 0 0 0 6.200 6.600H4.500c-.6 0-1 .4-1 1V10c0 .6.400 1 1 1H11V6.600h2V11h6.500c.6 0 1-.4 1-1V7.600c0-.6-.4-1-1-1h-1.700A3 3 0 0 0 12 3.800 3 3 0 0 0 9 2.500Zm0 2a1 1 0 0 1 1 1v1.100H9a1 1 0 0 1 0-2.100Zm6 0a1 1 0 0 1 0 2.100h-1V5.500a1 1 0 0 1 1-1ZM4.500 12.500v7c0 .8.700 1.500 1.500 1.500h5v-8.500H4.500Zm8.500 0V21h5c.8 0 1.500-.7 1.500-1.500v-7H13Z" />,
  },
  chat: {
    body: <path d="M4 6.500c0-1.100.9-2 2-2h12c1.100 0 2 .9 2 2v8c0 1.100-.9 2-2 2h-6.500L7 20v-3.500H6c-1.100 0-2-.9-2-2v-8ZM8 9.500h8M8 12.500h5" />,
  },
  pin: {
    body: (
      <>
        <path d="M12 21s6.500-5.700 6.500-11a6.500 6.500 0 1 0-13 0c0 5.300 6.500 11 6.500 11Z" />
        <circle cx="12" cy="10" r="2.300" />
      </>
    ),
  },
  pinOff: {
    body: (
      <>
        <path d="M7.300 5.500A6.500 6.500 0 0 1 18.500 10c0 1.600-.6 3.200-1.400 4.700M15 18c-1.600 1.800-3 3-3 3s-6.500-5.700-6.500-11c0-.6.100-1.200.2-1.800" />
        <path d="m4 4 16 16" />
      </>
    ),
  },
  locate: {
    body: (
      <>
        <circle cx="12" cy="12" r="3.200" />
        <path d="M12 2.500V6M12 18v3.500M21.500 12H18M6 12H2.500" />
      </>
    ),
  },
  pencil: { body: <path d="m4 20 1-4L16.300 4.700a1.500 1.500 0 0 1 2.100 0l.9.900a1.500 1.500 0 0 1 0 2.100L8 19l-4 1ZM14.500 6.500l3 3" /> },
  leaf: {
    fill: true,
    body: <path d="M20.500 3.500c-8.500-.5-14 2.500-15 8-.4 2.300.3 4.200 1.300 5.500L4 19.800 5.200 21l2.800-2.800c1.300 1 3.200 1.700 5.500 1.300 5.500-1 8.500-6.500 7-16ZM9.300 15.700c2-3.700 4.700-6 8.200-7.700-2.700 2.400-4.700 5-6.200 8.200l-2-.5Z" />,
  },
  wheat: { body: <path d="M12 21V8M12 8c0-2 1.200-3.700 3-4.500C15 5.500 13.800 7.200 12 8Zm0 0c0-2-1.200-3.700-3-4.500C9 5.500 10.200 7.200 12 8Zm0 4.500c0-2 1.500-3.500 3.500-4 0 2-1.500 3.500-3.500 4Zm0 0c0-2-1.500-3.500-3.500-4 0 2 1.500 3.500 3.500 4Zm0 4.500c0-2 1.500-3.500 3.500-4 0 2-1.500 3.500-3.500 4Zm0 0c0-2-1.500-3.500-3.500-4 0 2 1.500 3.500 3.500 4Z" /> },
  cube: { body: <path d="m12 3 8 4.200v9.600L12 21l-8-4.200V7.200L12 3Zm0 8.500 8-4.300M12 11.500 4 7.200M12 11.500V21" /> },
  pause: {
    fill: true,
    body: <path d="M12 2.500a9.500 9.500 0 1 0 0 19 9.500 9.500 0 0 0 0-19ZM9 8.200c0-.4.300-.7.700-.7h.6c.4 0 .7.300.7.700v7.600c0 .4-.3.700-.7.700h-.6a.7.700 0 0 1-.7-.7V8.200Zm4 0c0-.4.300-.7.700-.7h.6c.4 0 .7.300.7.700v7.600c0 .4-.3.700-.7.700h-.6a.7.700 0 0 1-.7-.7V8.200Z" />,
  },
  moon: { fill: true, body: <path d="M20.300 14.600A8.500 8.500 0 0 1 9.400 3.700a.600.600 0 0 0-.8-.7A9.500 9.500 0 1 0 21 15.400a.600.600 0 0 0-.7-.8Z" /> },
  clockAlert: {
    body: (
      <>
        <circle cx="11" cy="12" r="8" />
        <path d="M11 7.500V12l2.500 2M20.500 4.500v5M20.500 12.300v.2" />
      </>
    ),
  },
  receipt: { body: <path d="M6 3.500h12v17l-2-1.300-2 1.300-2-1.300-2 1.300-2-1.300-2 1.300v-17ZM9 8h6M9 11.500h6M9 15h3.500" /> },
  card: {
    body: (
      <>
        <rect x="3" y="5.500" width="18" height="13" rx="2.500" />
        <path d="M3 10h18M6.500 14.500h3" />
      </>
    ),
  },
  phone: { body: <path d="M6.500 3.500h3l1.500 4-2 1.500a11 11 0 0 0 6 6l1.500-2 4 1.500v3c0 1.100-.9 2-2 2A15.500 15.500 0 0 1 4.500 5.500c0-1.100.9-2 2-2Z" /> },
  star: { body: <path d="m12 3.500 2.600 5.400 5.900.8-4.300 4.100 1 5.900L12 16.900l-5.200 2.800 1-5.900-4.300-4.100 5.900-.8L12 3.500Z" /> },
  starFill: { fill: true, body: <path d="m12 2.800 2.800 5.700 6.300.9-4.600 4.400 1.100 6.300L12 17.100l-5.600 3 1.100-6.300-4.600-4.400 6.300-.9L12 2.800Z" /> },
  send: { body: <path d="M12 19.500V5M6 10.500l6-6 6 6" /> },
  camera: {
    body: (
      <>
        <path d="M4.500 8h3l1.300-2.500h6.400L16.500 8h3c.6 0 1 .4 1 1v9.500c0 .6-.4 1-1 1h-15c-.6 0-1-.4-1-1V9c0-.6.400-1 1-1Z" />
        <circle cx="12" cy="13.300" r="3.300" />
      </>
    ),
  },
  globe: {
    body: (
      <>
        <circle cx="12" cy="12" r="8.500" />
        <path d="M3.500 12h17M12 3.500c2.500 2.300 3.800 5.200 3.800 8.500s-1.300 6.200-3.800 8.500c-2.500-2.300-3.800-5.200-3.800-8.500s1.300-6.200 3.800-8.500Z" />
      </>
    ),
  },
  logout: { body: <path d="M14 4.500h3.500c.6 0 1 .4 1 1v13c0 .6-.4 1-1 1H14M10 8l-4 4 4 4M6 12h9" /> },
  warning: {
    fill: true,
    body: <path d="M12 2.500a9.500 9.500 0 1 0 0 19 9.500 9.500 0 0 0 0-19Zm-1 5a1 1 0 0 1 2 0v5a1 1 0 0 1-2 0v-5Zm1 9.800a1.200 1.200 0 1 1 0-2.400 1.200 1.200 0 0 1 0 2.400Z" />,
  },
  basket: { body: <path d="M3.500 9.500h17l-1.700 9c-.1.600-.6 1-1.200 1H6.400c-.6 0-1.100-.4-1.200-1l-1.700-9ZM8 9.500l3-5.500M16 9.500 13 4M9 13v3.500M12 13v3.500M15 13v3.500" /> },
  bag: { body: <path d="M5.500 8h13l1 11.500c0 .6-.4 1-1 1h-13c-.6 0-1-.4-1-1l1-11.500ZM9 10.500V7a3 3 0 0 1 6 0v3.500" /> },
  door: {
    body: (
      <>
        <path d="M6.500 20.500v-16c0-.6.400-1 1-1h9c.6 0 1 .4 1 1v16M4 20.500h16" />
        <circle cx="14.300" cy="12.500" r="0.700" fill="currentColor" />
      </>
    ),
  },
  expand: { body: <path d="M14.500 4h5.500v5.500M9.500 20H4v-5.500M20 4l-6.500 6.500M4 20l6.500-6.500" /> },
  calendar: {
    body: (
      <>
        <rect x="3.500" y="5" width="17" height="15.500" rx="2.500" />
        <path d="M3.500 10h17M8 3v4M16 3v4" />
      </>
    ),
  },
  play: { fill: true, body: <path d="M8 5.200c0-.8.900-1.300 1.500-.9l10.300 6.800c.6.4.6 1.300 0 1.700L9.500 19.600c-.7.400-1.500 0-1.500-.9V5.200Z" /> },
  cartPlus: {
    fill: true,
    body: <path d="M2.500 3.500a1 1 0 0 0 0 2h1.700l2.300 10.200c.2.800.9 1.300 1.700 1.300h9.300a1 1 0 0 0 0-2H8.400l-.3-1.500h9.700c.8 0 1.500-.5 1.700-1.300l.9-3.700h-2.100l-.7 3H7.600L6.300 5.500C6 4.300 5 3.500 3.800 3.500H2.500ZM16 2.500a1 1 0 0 0-1 1V5h-1.500a1 1 0 0 0 0 2H15v1.500a1 1 0 0 0 2 0V7h1.500a1 1 0 0 0 0-2H17V3.500a1 1 0 0 0-1-1ZM9 18.500a1.700 1.700 0 1 0 0 3.400 1.700 1.700 0 0 0 0-3.400Zm8 0a1.700 1.700 0 1 0 0 3.400 1.700 1.700 0 0 0 0-3.400Z" />,
  },
  doorExit: { body: <path d="M10 4.500H6.500c-.6 0-1 .4-1 1v13c0 .6.400 1 1 1H10M14 8l4 4-4 4M18 12H9" /> },
  shield: { body: <path d="M12 3 5 5.800V11c0 4.500 2.900 8.300 7 10 4.100-1.700 7-5.500 7-10V5.800L12 3Zm-3 9 2.200 2.200L15.500 10" /> },
  doc: { body: <path d="M7 3.500h7l4 4V19.500c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1v-15c0-.6.400-1 1-1ZM14 3.500v4h4M9 12h6M9 15.500h6" /> },
  pointer: { fill: true, body: <path d="M20.200 3.200 3.900 10c-.8.300-.8 1.500.1 1.800l6.300 1.900 1.900 6.300c.3.900 1.500.9 1.800.1l6.800-16.300c.2-.4-.2-.8-.6-.6Z" /> },
} satisfies Record<string, Shape>;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 20, weight = 2, className }: { name: IconName; size?: number; weight?: number; className?: string | undefined }) {
  const shape: Shape = ICONS[name];
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...(shape.fill
        ? { fill: 'currentColor', fillRule: 'evenodd' as const }
        : { fill: 'none', stroke: 'currentColor', strokeWidth: weight, strokeLinecap: 'round', strokeLinejoin: 'round' })}
    >
      {shape.body}
    </svg>
  );
}
